/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import StyledBox from 'views/tours-module/TourReviewsView/TourReviewsView.styles';
import { useTranslation } from 'next-i18next';
import { PageTitle } from 'views/common/components';
import { ReviewsSections } from 'views/tours-module/TourReviewsView/components';
import PATHS from 'lib/navigation/paths';
import axios, { API } from 'lib/config/axios';
import { setLoading, setSnackbar } from 'redux/features/ui-slice';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import generateMetaData from 'lib/utils/generateMetaData';
import { TOUR_TYPES } from 'lib/utils/CONSTANTS';
import Head from 'next/head';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import { TourReviewsViewSkeleton } from 'views/tours-module/TourReviewsView/TourReviewsViewSkeleton';
import ReservationWrapper from 'views/tours-module/TourView/components/ReservationWrapper';
import {
  Container, Grid, Box, Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const TourReviewsView = (props) => {
  const { serverData, tourType } = props;
  const { t } = useTranslation('fe_er_tours_reviews_page');
  const dispatch = useDispatch();
  const appNavigator = useAppNavigator();
  const handleErrorResponse = useErrorsHandler();
  const [data, setData] = useState(serverData);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [includeBike, setIncludeBike] = useState();

  const getBreadcrumbsObject = () => {
    const tourTypeBreadcrumb = (() => {
      switch (tourType) {
        case TOUR_TYPES.GUIDED:
          return {
            text: t('fe_er_tours_reviews_page:guided_motorcycle_tours'),
            path: PATHS.tour.guidedTourIndex(),
          };
        case TOUR_TYPES.SELF_DRIVE:
          return {
            text: t('fe_er_tours_reviews_page:self_drive_motorcycle_tours'),
            path: PATHS.tour.selfTourIndex(),
          };
        case TOUR_TYPES.GUIDED_OFFROAD:
          return {
            text: t('fe_er_tours_reviews_page:off_road_tours'),
            path: PATHS.guidedOffroadTour.index(),
          };
        default:
          return null;
      }
    })();

    const tourPageBreadcrumb = (() => {
      switch (tourType) {
        case TOUR_TYPES.GUIDED:
          return {
            text: data.tour?.name,
            path: PATHS.guidedTour(appNavigator.query.slug),
          };
        case TOUR_TYPES.SELF_DRIVE:
          return {
            text: data.tour?.name,
            path: PATHS.selfDriveTour(appNavigator.query.slug),
          };
        case TOUR_TYPES.GUIDED_OFFROAD:
          return {
            text: data.tour?.name,
            path: PATHS.guidedOffroadTour.slug(appNavigator.query.slug),
          };
        default:
          return null;
      }
    })();

    return {
      links: [
        {
          text: t('fe_er_tours_reviews_page:EagleRider'),
          path: PATHS.home(),
        },
        {
          text: t('fe_er_tours_reviews_page:motorcycle_tours'),
          path: PATHS.motorcycleTours.index(),
        },
        {
          text: t('fe_er_tours_reviews_page:all_motorcycle_tours'),
          path: PATHS.motorcycleTours.listing(),
        },
        tourTypeBreadcrumb,
        tourPageBreadcrumb,
        {
          text: t('fe_er_tours_reviews_page:tour_reviews', {
            tour: data.tour?.name,
          }),
        },
      ],
    };
  };
  const convertBookingParams = (params) => Object.entries(params).reduce((obj, [key, val]) => {
    const newKey = key.slice(12, key.length - 1);
    return {
      ...obj,
      [newKey]: val,
    };
  }, {});
  const getBookingDetails = (tourId, params, firstReq, reservationType) => {
    dispatch(setLoading(true));
    let withMotorcycle = params.with_motorcycle;
    if (firstReq) {
      if (reservationType === 'without_motorcycle_reservations_only') {
        withMotorcycle = false;
      } else withMotorcycle = true;
    }
    axios
      .get(API.tours.bookingDetails.replace('{tour_id}', tourId), {
        params: {
          with_motorcycle: withMotorcycle,
          occurrence_id: params.occurrence_id && +params.occurrence_id,
          package_id: params.package_id && +params.package_id,
          packages_count: params.packages_count && +params.packages_count,
          pickup_date: params.pickup_date,
        },
      })
      .then((res) => {
        setBookingDetails(res.data);
        dispatch(setLoading(false));
      })
      .catch((error) => {
        dispatch(setLoading(false));
        if (error.response.status == 403) {
          if (bookingDetails) {
            setBookingDetails((prev) => ({
              ...prev,
              pickup_date: params.pickup_date,
              packages: [],
            }));

            dispatch(
              setSnackbar({
                open: true,
                severity: 'error',
                message: t('fe_er_tours_reviews_page:no_available_packages'),
              }),
            );
          } else if (includeBike === undefined) setIncludeBike(true);
        } else {
          handleErrorResponse(error, false);
        }
      });
  };
  const getTourReviewsData = (page) => {
    const {
      guided_motorcycle_tours_segment,
      self_drive_motorcycle_tours_segment,
      off_road_tours_segment,
      slug,
      ...bookingParams
    } = {
      ...appNavigator.query,
    };
    axios
      .get(API.tours.reviews.replace('{identifier}', slug), {
        params: {
          tour_type_slug: decodeURIComponent(
            guided_motorcycle_tours_segment
              || self_drive_motorcycle_tours_segment
              || off_road_tours_segment,
          ),
          page,
        },
      })
      .then((res) => {
        setData(res.data);
        const convertedParams = convertBookingParams(bookingParams);
        getBookingDetails(
          res.data.tour.id,
          convertedParams,
          true,
          res.data.tour.acceptable_reservations_type,
        );
      })
      .catch((err) => {
        switch (err.response?.status) {
          case 403:
            switch (err.response.data.error_code) {
              case 'ER0001':
                appNavigator.push(PATHS.motorcycleTours.index());
                break;
              case 'ER0002':
                appNavigator.push(
                  PATHS.tour.reviews(
                    `/${err.response.data.correct_slugs.tour_type_slug}/${err.response.data.correct_slugs.tour_slug}`,
                  ),
                );
                break;
              default:
                handleErrorResponse(err, false);
                break;
            }
            break;
          default:
            handleErrorResponse(err, false);
            break;
        }
      });
  };
  const handlePaginationClick = (page) => {
    switch (tourType) {
      case TOUR_TYPES.GUIDED:
        return appNavigator.push(
          PATHS.tour.guidedTourReviews(appNavigator.query.slug, { page }),
        );
      case TOUR_TYPES.SELF_DRIVE:
        return appNavigator.push(
          PATHS.tour.selfTourReviews(appNavigator.query.slug, { page }),
        );
      case TOUR_TYPES.GUIDED_OFFROAD:
        return appNavigator.push(
          PATHS.tour.offRoadTourReviews(appNavigator.query.slug, { page }),
        );
      default:
        break;
    }
    return null;
  };

  const addNewReview = (reviews) => {
    setData({
      ...data,
      users_reviews: reviews,
    });
  };

  const getTourTitle = () => {
    switch (tourType) {
      case TOUR_TYPES.GUIDED:
        return `${data.tour.name} ${t(
          'fe_er_tours_reviews_page:guided_motorcycle_tours',
        )} ${t('fe_er_tours_reviews_page:reviews')}`;
      case TOUR_TYPES.SELF_DRIVE:
        return `${data.tour.name} ${t(
          'fe_er_tours_reviews_page:self_drive_motorcycle_tours',
        )} ${t('fe_er_tours_reviews_page:reviews')}`;
      case TOUR_TYPES.GUIDED_OFFROAD:
        return `${data.tour.name} ${t(
          'fe_er_tours_reviews_page:off_road_tours',
        )} ${t('fe_er_tours_reviews_page:reviews')}`;
      default:
        break;
    }
    return null;
  };

  const getInitialIncludeBikeState = () => {
    if (data.tour.acceptable_reservations_type === 'any_reservations') {
      if (bookingDetails?.selected_package_id) {
        const idx = bookingDetails.packages.findIndex(
          (pack) => pack.id === bookingDetails.selected_package_id,
        );
        return !!bookingDetails.packages[idx].motorcycles_count;
      }
      return true;
    }

    return (
      data.tour.acceptable_reservations_type
      === 'with_motorcycle_reservations_only'
    );
  };
  const handleBackToTourBtnClick = () => {
    switch (tourType) {
      case TOUR_TYPES.GUIDED:
        appNavigator.push(PATHS.guidedTour(appNavigator.query.slug));
        break;
      case TOUR_TYPES.SELF_DRIVE:
        appNavigator.push(PATHS.selfDriveTour(appNavigator.query.slug));
        break;
      case TOUR_TYPES.GUIDED_OFFROAD:
        appNavigator.push(
          PATHS.guidedOffroadTour.slug(appNavigator.query.slug),
        );
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    if (typeof window !== 'undefined' && !serverData) {
      getTourReviewsData(appNavigator.query.page);
    }
  }, [
    appNavigator.query.page,
    appNavigator.query.slug,
    appNavigator.query.guided_motorcycle_tours_segment
      || appNavigator.query.self_drive_motorcycle_tours_segment
      || appNavigator.query.off_road_tours_segment,
  ]);

  useEffect(() => {
    if (bookingDetails && typeof includeBike === 'undefined') {
      setIncludeBike(getInitialIncludeBikeState());
    }
  }, [bookingDetails]);

  return (
    <>
      {data ? (
        <StyledBox className="overFlowXHidden">
          {data.seo_values && <Head>{generateMetaData(data.seo_values)}</Head>}
          <Container maxWidth="xl">
            <PageTitle
              mainTitleObject={{
                name: getTourTitle(),
              }}
              breadcrumbsObject={getBreadcrumbsObject()}
              shareList={false}
              titlewidth="12"
            />
            <Button
              className="TRVBackBtn"
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToTourBtnClick}>
              {t('fe_er_tours_reviews_page:back_to_tour')}
            </Button>
            <Box className="TRVReviewAdWidgetContiner">
              <Grid
                container
                rowSpacing={2}
                columnSpacing={{
                  xs: 0,
                  sm: 0,
                  md: 3,
                  lg: 5,
                  xl: 10,
                }}>
                <Grid item xl={8} lg={8} md={12} sm={12}>
                  <ReviewsSections
                    reviews={data.users_reviews}
                    handlePaginationClick={handlePaginationClick}
                    getTourReviewsData={getTourReviewsData}
                    addNewReview={addNewReview}
                    tourName={data.tour.name}
                    tourId={data.tour.id}
                  />
                </Grid>
                <Grid
                  item
                  xl={4}
                  lg={4}
                  md={12}
                  sm={12}
                  className="showDESKTOP">
                  <ReservationWrapper
                    bookingDetails={bookingDetails}
                    tourId={data.tour.id}
                    startingPrice={data.tour.starting_price}
                    includeBike={includeBike}
                    onIncludeBikeChange={setIncludeBike}
                    getBookingDetails={getBookingDetails}
                    acceptableReservationsType={
                      data.tour.acceptable_reservations_type
                    }
                    tourType={data.tour.tour_type}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(data?.structured_data),
              }}
            /> */}
          </Container>
        </StyledBox>
      ) : (
        <TourReviewsViewSkeleton />
      )}
    </>
  );
};

export { TourReviewsView };
